/**
 * <MyIssues> — the issues somebody reported (or watches), and, for an
 * organization's administrator, everything their organization reported.
 *
 * Rows lead with what needs the READER: an issue waiting for their answer is
 * flagged first, because that is the one thing on the list only they can move.
 */

import { useCallback, useEffect, useState } from 'react'
import { AlertTriangle, CloudOff, HelpCircle, RefreshCw } from 'lucide-react'

import type { IssueSummary, QueuedListItem, SupportMeta, SupportRuntime } from './contract'
import {
  Empty, ErrorLine, Pill, Spinner, SupportScope, btn, errorText, mutedText, severityTone,
  stateLabel, statusTone, timeAgo, vocabLabel,
} from './ui'

export interface MyIssuesProps {
  runtime: SupportRuntime
  meta?: SupportMeta | null
  onOpen: (issueNumber: number) => void
  /** Bump to force a reload (e.g. after a new report). */
  refreshKey?: number
}

export default function MyIssues({ runtime, meta, onOpen, refreshKey = 0 }: MyIssuesProps) {
  const orgAdmin = !!meta?.me.is_org_admin && !meta?.me.sees_all_orgs
  const [scope, setScope] = useState<'mine' | 'org'>('mine')
  const [openOnly, setOpenOnly] = useState(true)
  const [rows, setRows] = useState<IssueSummary[] | null>(null)
  // Reports a deployed runtime is still holding (support service unreachable).
  const [queued, setQueued] = useState<QueuedListItem[]>([])
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await runtime.listIssues({ scope, open: openOnly, limit: 200 })
      setRows(res.issues || [])
      setQueued(res.queued || [])
      setError('')
    } catch (e) {
      setError(errorText(e, 'Your issues could not be loaded.'))
    } finally {
      setLoading(false)
    }
  }, [runtime, scope, openOnly])

  useEffect(() => { void load() }, [load, refreshKey])

  const lifecycle = meta?.lifecycle
  const sorted = (rows || []).slice().sort((a, b) => {
    const wa = a.status === 'needs_info' ? 0 : 1
    const wb = b.status === 'needs_info' ? 0 : 1
    return wa - wb || String(b.updated_at || '').localeCompare(String(a.updated_at || ''))
  })

  return (
    <SupportScope>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
        {orgAdmin && (
          <>
            <button type="button" style={btn(scope === 'mine' ? 'subtle' : 'ghost', true)} onClick={() => setScope('mine')}>Mine</button>
            <button type="button" style={btn(scope === 'org' ? 'subtle' : 'ghost', true)} onClick={() => setScope('org')}>My organization</button>
          </>
        )}
        <label style={{ ...mutedText, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
          <input type="checkbox" checked={openOnly} onChange={(e) => setOpenOnly(e.target.checked)} /> Open only
        </label>
        <span style={{ flex: 1 }} />
        <button type="button" style={btn('ghost', true)} onClick={() => { void load() }} title="Refresh">
          {loading ? <Spinner size={12} /> : <RefreshCw size={12} />}
        </button>
      </div>
      <ErrorLine message={error} onRetry={() => { void load() }} />
      {rows === null && !error && <div style={{ display: 'flex', gap: 8 }}><Spinner /> Loading…</div>}
      {queued.length > 0 && (
        <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 6px' }}>
          {queued.map((q) => (
            <li key={String(q.outbox_id)} style={{
              padding: '0.55rem 0.65rem', marginBottom: 6, borderRadius: 9,
              border: '1px dashed var(--sp-warning)', background: 'var(--sp-bg)',
            }}>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                <Pill tone="warning"><CloudOff size={10} /> waiting to send</Pill>
                {q.severity && <Pill tone={severityTone(meta, q.severity)}>{vocabLabel(meta, 'severities', q.severity)}</Pill>}
                <span style={{ flex: 1 }} />
                <span style={mutedText}>{timeAgo(q.created_at)}</span>
              </div>
              <div style={{ fontWeight: 600, marginTop: 4 }}>{q.title || 'Untitled report'}</div>
              <div style={mutedText}>
                Saved here because the support service could not be reached; it is sent automatically.
                {q.attempts ? ` ${q.attempts} attempt(s) so far.` : ''}
              </div>
            </li>
          ))}
        </ul>
      )}
      {rows !== null && rows.length === 0 && queued.length === 0 && (
        <Empty>{openOnly ? 'Nothing open. Anything you report shows up here.' : 'You have not reported anything yet.'}</Empty>
      )}
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {sorted.map((i) => {
          const waiting = i.status === 'needs_info'
          return (
            <li key={i.id}>
              <button type="button" onClick={() => onOpen(i.number)} style={{
                display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer',
                padding: '0.55rem 0.65rem', marginBottom: 6, borderRadius: 9,
                border: `1px solid ${waiting ? 'var(--sp-accent)' : 'var(--sp-border)'}`,
                background: waiting ? 'var(--color-accent-weak, rgba(76,141,255,0.08))' : 'var(--sp-bg)',
                color: 'var(--sp-text)',
              }}>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ ...mutedText, fontWeight: 700 }}>#{i.number}</span>
                  <Pill tone={statusTone(lifecycle, i.status)}>{stateLabel(lifecycle, i.status)}</Pill>
                  <Pill tone={severityTone(meta, i.severity)}>{vocabLabel(meta, 'severities', i.severity)}</Pill>
                  {i.sla_overdue && <Pill tone="error"><AlertTriangle size={10} /> overdue</Pill>}
                  <span style={{ flex: 1 }} />
                  <span style={mutedText}>{timeAgo(i.updated_at)}</span>
                </div>
                <div style={{ fontWeight: 600, marginTop: 4 }}>{i.title}</div>
                {waiting && (
                  <div style={{ fontSize: '0.8225rem', color: 'var(--sp-accent)', marginTop: 3 }}>
                    <HelpCircle size={11} style={{ verticalAlign: -1 }} /> Waiting for your answer
                  </div>
                )}
                {scope === 'org' && i.reporter_name && (
                  <div style={mutedText}>Reported by {i.reporter_name}</div>
                )}
              </button>
            </li>
          )
        })}
      </ul>
    </SupportScope>
  )
}
